import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AuthLoading } from '@/components/auth/AuthGuard';
import AuthShell from '@/components/auth/AuthShell';

export default function LoginPage() {
  const { session, profile, loading, profileLoading, signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (loading || (session && profileLoading)) return <AuthLoading />;
  if (session) return <Navigate to={profile?.status === 'approved' ? '/' : '/pending'} replace />;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (!trimmed || !password) {
      setError('이메일과 비밀번호를 모두 입력해 주세요.');
      return;
    }
    setSubmitting(true);
    const { error: err } = await signIn(trimmed, password);
    setSubmitting(false);
    if (err) setError(err);
  };

  return (
    <AuthShell
      title="로그인"
      subtitle="가입한 이메일로 로그인하면 나만의 오답노트를 이어서 사용할 수 있어요."
      footer={
        <>
          아직 계정이 없나요?{' '}
          <Link to="/signup" className="font-bold text-primary-700 hover:underline">
            회원가입 신청
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <div>
          <label htmlFor="login-email" className="mb-1.5 block text-xs font-semibold text-foreground-600">
            이메일
          </label>
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 transition focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100">
            <i className="ri-mail-line text-base text-foreground-400"></i>
            <input
              id="login-email"
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none"
            />
          </div>
        </div>

        <div>
          <label htmlFor="login-password" className="mb-1.5 block text-xs font-semibold text-foreground-600">
            비밀번호
          </label>
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 transition focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100">
            <i className="ri-lock-line text-base text-foreground-400"></i>
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded text-foreground-400 transition hover:bg-background-200 hover:text-foreground-700"
              aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
            >
              <i className={showPassword ? 'ri-eye-off-line text-base' : 'ri-eye-line text-base'}></i>
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-xs leading-relaxed text-accent-800">
            <i className="ri-error-warning-line mt-0.5 text-sm"></i>
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="mt-1 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-bold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base"></i>
              로그인 중...
            </>
          ) : (
            <>
              <i className="ri-login-box-line text-base"></i>
              로그인
            </>
          )}
        </button>
      </form>
    </AuthShell>
  );
}